import { AttendanceStatus, Terminal } from './../models/Attendance';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ResponesModel, ResponseData } from 'src/app/models/ResponseData';
import * as g from '../../../globals';
import { LeaveRequest, LeaveToUpdate } from '../models/Leaves';
import { ConditionOperator, ListPayload } from '../models/ListPayload';
import { FilterField } from '../models/FilterField';
import { EmployeeStatus } from '../models/Employee';
import { FieldDataType } from '@app/models/module-structure';

@Injectable()
export class TimeService {
  leavesUrl = `${g.apiBaseUrl}Leaves`;
  attendanceUrl = `${g.apiBaseUrl}Attendance`;
  timeSheetUrl = `${g.apiBaseUrl}TimesheetApi`;
  configUrl = `${g.apiBaseUrl}Configuration`;
  constructor(public http: HttpClient) {}

  //#region Timesheet
  getTimeLogs(filterFields: FilterField[]) {
    return this.http.post<ResponseData<any>>(
      `${this.timeSheetUrl}/GetTimesheetLogs`,
      filterFields
    );
  }
  getEmpTimes() {
    return this.http.get(`${this.timeSheetUrl}/GetEmpTimes`);
  }
  //#endregion

  //#region Leaves
  getEmployees() {
    return this.http.get(`${this.leavesUrl}/GetEmployees`);
  }
  getLeavesByStatus(
    payload: ListPayload,
    status: number,
    empId = 0,
    showAll = false
  ): Observable<ResponseData<LeaveRequest[]>> {
    return this.http.post<ResponseData<LeaveRequest[]>>(
      `${this.leavesUrl}/GetLeavesByStatus`,
      {
        payload,
        status,
        empId,
        showAll,
      }
    );
  }
  getSupervisedRequests(payload: ListPayload) {
    return this.http.post<ResponseData<LeaveRequest[]>>(
      `${this.leavesUrl}/GetSupervisedLeaveRequests`,
      payload
    );
  }
  getSupervisedRequestsHistory(payload: ListPayload) {
    return this.http.post<ResponseData<LeaveRequest[]>>(
      `${this.leavesUrl}/SupervisedLeavesHistory`,
      payload
    );
  }
  getEmployeeLeavesList(payload: ListPayload, empId = 0) {
    return this.http.post<ResponseData<LeaveRequest[]>>(
      `${this.leavesUrl}/GetEmployeeLeavesList`,
      {
        payload,
        empId,
      }
    );
  }
  getEmployeeBalance(empId = 0) {
    return this.http.get(`${this.leavesUrl}/GetEmployeeBalance?empId=${empId}`);
  }
  updateLeavesStatus(
    leavesToUpdate: LeaveToUpdate[],
    status
  ): Observable<ResponseData<boolean>> {
    return this.http.post<ResponseData<boolean>>(
      `${this.leavesUrl}/UpdateLeavesStatus?status=${status}`,
      leavesToUpdate
    );
  }
  //#endregion

  //#region Attendance
  getColleaguesStatus(): Observable<ResponseData<EmployeeStatus[]>> {
    return this.http.get<ResponseData<EmployeeStatus[]>>(
      `${this.attendanceUrl}/ColleaguesStatus`
    );
  }
  getTerminalsList(payload: ListPayload) {
    return this.http.post<ResponseData<Terminal[]>>(
      `${this.attendanceUrl}/GetTerminalsList`,
      {
        payload,
      }
    );
  }
  getTerminalById(id: number) {
    return this.http.get(`${this.attendanceUrl}/GetTerminalById?id=${id}`);
  }
  generateRestoreKey(deviceId: string): Observable<{ key: string }> {
    const id = encodeURIComponent(deviceId);
    return this.http.get<{ key: string }>(
      `${this.attendanceUrl}/GenerateRestoreKey?deviceId=${id}`
    );
  }
  changeTerminalActivation(id: string, status: string) {
    return this.http.post<ResponseData<boolean>>(
      `${this.attendanceUrl}/ChangeTerminalActivation`,
      { id, status }
    );
  }
  updateTerminalData(terminal: Terminal) {
    return this.http.post<ResponseData<boolean>>(
      `${this.attendanceUrl}/UpdateTerminalFromWeb`,
      terminal
    );
  }
  getAttendanceLogsByDate(
    startDate: Date,
    finishDate: Date,
    empId = 0
  ): Observable<ResponesModel<any>> {
    return this.http.post<ResponesModel<any>>(
      `${this.attendanceUrl}/EmployeeHistoryByDate`,
      { startDate, finishDate, empId }
    );
  }
  SupervisedEmployeesHistory(
    startDate: Date,
    finishDate: Date
  ): Observable<ResponseData<any>> {
    const listPayload: ListPayload = {
      IsAsc: false,
      OrderByField: 'Date',
      PageIndex: 0,
      PageSize: 100000000,
      Conditions: [
        {
          Key: 'Date',
          Type: FieldDataType.Date,
          Value: startDate,
          Operator: ConditionOperator.greaterOrEquals,
        },
        {
          Key: 'Date',
          Type: FieldDataType.Date,
          Value: finishDate,
          Operator: ConditionOperator.lessOrEquals,
        },
      ],
    };
    return this.http.post<ResponseData<any>>(
      `${this.attendanceUrl}/SupervisedEmployeesHistory`,
      listPayload
    );
  }
  getEmployeeStatus(empId = '', terminalId = '') {
    return this.http.get(
      `${this.attendanceUrl}/LoggedEmployeeStatus?empId=${empId}&terminalId=${terminalId}`
    );
  }

  getAllStatuses(): Observable<ResponseData<AttendanceStatus[]>> {
    return this.http.get<ResponseData<AttendanceStatus[]>>(
      `${this.attendanceUrl}/GetAttendanceStatuses`
    );
  }

  //#endregion

  approveTimeRecord(
    isCustomer: boolean,
    absEntry: number,
    lineId: number,
    message: string,
    isApproved: boolean
  ) {
    const type = isCustomer ? 'customer' : 'manager';
    return this.http.get(
      `${this.timeSheetUrl}/Approve?type=${type}&absEntry=${absEntry}&lineID=${lineId}&message=${message}&status=${isApproved}`
    );
  }
}
